import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'

import { bakeLensMap, LENS_CHROMA, LENS_MAP_SCALE } from '@/lib/frieze/lens'
import { CA_LEVELS } from '@/constants/frieze'

/**
 * Filter definitions for the wall (§11 & §12):
 *   17 × chromatic-aberration levels + 1 × liquid-glass loupe.
 *
 * The level presets are derived deterministically at module scope so the server
 * and the client render identical markup.
 */
export const FriezeDefs: FC = (): ReactNode => {
    const mapRef = useRef<SVGFEImageElement | null>(null)

    useEffect(() => {
        const url = bakeLensMap()

        if (url && mapRef.current) mapRef.current.setAttribute('href', url)
    }, [])

    return (
        <svg
            width={0}
            height={0}
            aria-hidden='true'
            focusable='false'
            className='pointer-events-none absolute'
        >
            <defs>
                {CA_LEVELS.map(({ level, blur, dx }) => (
                    <filter
                        key={level}
                        id={`fz-ca-${level}`}
                        x='-45%'
                        y='-30%'
                        width='190%'
                        height='160%'
                        colorInterpolationFilters='sRGB'
                    >
                        <feGaussianBlur in='SourceGraphic' stdDeviation={`${blur} 0`} result='b' />

                        <feOffset in='b' dx={-dx} dy='0' result='ro' />
                        <feColorMatrix
                            in='ro'
                            type='matrix'
                            values='1 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 1 0'
                            result='r'
                        />

                        <feOffset in='b' dx={dx} dy='0' result='bo' />
                        <feColorMatrix
                            in='bo'
                            type='matrix'
                            values='0 0 0 0 0  0 0 0 0 0  0 0 1 0 0  0 0 0 1 0'
                            result='bl'
                        />

                        <feColorMatrix
                            in='b'
                            type='matrix'
                            values='0 0 0 0 0  0 1 0 0 0  0 0 0 0 0  0 0 0 1 0'
                            result='g'
                        />

                        <feBlend in='r' in2='g' mode='screen' result='rg' />
                        <feBlend in='rg' in2='bl' mode='screen' />
                    </filter>
                ))}

                <filter
                    id='fz-lens-glass'
                    filterUnits='userSpaceOnUse'
                    primitiveUnits='userSpaceOnUse'
                    x='0'
                    y='0'
                    width='75'
                    height='75'
                    colorInterpolationFilters='sRGB'
                >
                    <feImage
                        ref={mapRef}
                        x='0'
                        y='0'
                        width='75'
                        height='75'
                        preserveAspectRatio='none'
                        result='map'
                    />

                    <feDisplacementMap
                        in='SourceGraphic'
                        in2='map'
                        scale={LENS_MAP_SCALE + LENS_CHROMA}
                        xChannelSelector='R'
                        yChannelSelector='G'
                        result='dr'
                    />
                    <feColorMatrix
                        in='dr'
                        type='matrix'
                        values='1 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 1 0'
                        result='cr'
                    />

                    <feDisplacementMap
                        in='SourceGraphic'
                        in2='map'
                        scale={LENS_MAP_SCALE}
                        xChannelSelector='R'
                        yChannelSelector='G'
                        result='dg'
                    />
                    <feColorMatrix
                        in='dg'
                        type='matrix'
                        values='0 0 0 0 0  0 1 0 0 0  0 0 0 0 0  0 0 0 1 0'
                        result='cg'
                    />

                    <feDisplacementMap
                        in='SourceGraphic'
                        in2='map'
                        scale={LENS_MAP_SCALE - LENS_CHROMA}
                        xChannelSelector='R'
                        yChannelSelector='G'
                        result='db'
                    />
                    <feColorMatrix
                        in='db'
                        type='matrix'
                        values='0 0 0 0 0  0 0 0 0 0  0 0 1 0 0  0 0 0 1 0'
                        result='cb'
                    />

                    <feBlend in='cr' in2='cg' mode='screen' result='crg' />
                    <feBlend in='crg' in2='cb' mode='screen' />
                </filter>
            </defs>
        </svg>
    )
}
